import { Link } from '@tanstack/react-router'
import { ArrowRightIcon } from 'lucide-react'
import { useEffect } from 'react'

import {
  DocumentProvider,
  PagePreview,
  useCurrentPage,
  useDocument,
  useDocumentActions,
  useDocumentPages,
  useFetchDocument,
} from '@/modules/documents'
import { getDocumentPath } from '@/utils'

interface SearchPagePreviewInnerProps {
  documentId: number
  pageNumber: number
  blockRef: string
}

const SearchPagePreviewInner = ({
  documentId,
  pageNumber,
  blockRef,
}: SearchPagePreviewInnerProps) => {
  const pages = useDocumentPages()
  const currentPage = useCurrentPage()
  const document = useDocument()
  const { setCurrentPageFromNumber, setCurrentBlock } = useDocumentActions()

  useFetchDocument(String(documentId))

  useEffect(() => {
    if (pages.length > 0) {
      setCurrentPageFromNumber(pageNumber)
    }
  }, [pages.length, pageNumber, blockRef])

  useEffect(() => {
    setCurrentBlock(
      currentPage?.blocks.findIndex(({ ref }) => ref === blockRef),
    )
  }, [currentPage, blockRef])

  return (
    <div className="flex flex-col h-full">
      {document && (
        <div className="flex gap-2 rounded-t-lg border border-b-0 px-3 py-2 justify-between text-sm items-center h-13.25">
          <Link
            to={getDocumentPath(document)}
            className="font-medium inline-flex items-center gap-1 group"
          >
            <span className="inline-block truncate">{document?.fileName}</span>
            <span className="group-hover:text-foreground group-hover:bg-neutral-200/60 rounded p-1 transition-colors">
              <ArrowRightIcon className="size-4" />
            </span>
          </Link>
          Page {currentPage.number + 1}
        </div>
      )}

      <PagePreview
        isLoading={pages.length === 0 || !currentPage}
        showBlocks={false}
        showThumbnails={false}
        disableMouseEvents
      />
    </div>
  )
}

interface SearchPagePreviewProps {
  blockRef: string
  documentId: number
  pageNumber: number
}

export const SearchPagePreview = ({
  blockRef,
  documentId,
  pageNumber,
}: SearchPagePreviewProps) => (
  <DocumentProvider key={documentId}>
    <SearchPagePreviewInner
      documentId={documentId}
      pageNumber={pageNumber}
      blockRef={blockRef}
    />
  </DocumentProvider>
)
